// ─── Shared ConflictHeader for sync conflict views ──────────────────────────
//
// Extracted from repeated header card JSX across all 6 entity conflict views.
// Renders the glass-depth header with gradient accent, pulse dot, entity label,
// primary identifier, optional subtitle, badge, device ID, and conflict message.

import type { ReactNode } from 'react';
import { Badge } from '../../BasicComponents/Badge';
import { GLASS, cn, toneClasses } from '../../../constants/design-tokens';
import type { Tone } from '../../../constants/design-tokens';
import type { StatusKey } from '../../../constants/status-colors';

export interface ConflictHeaderAccent {
  from: string;   // e.g. 'from-brand-500/10'
  to: string;     // e.g. 'to-indigo-500/10'
  ring: string;   // e.g. 'ring-brand-500/20'
  text: string;   // e.g. 'text-brand-700'
  darkText: string; // e.g. 'dark:text-brand-300'
}

export interface ConflictHeaderProps {
  entityLabel: string;
  identifier: string;
  accent: ConflictHeaderAccent;
  tone: Tone;
  badgeVariant: StatusKey;
  badgeLabel?: string;
  subtitle?: ReactNode;
  deviceId: string | null;
  conflictMessage?: string;
  ariaLabel: string;
}

export function ConflictHeader({
  entityLabel,
  identifier,
  accent,
  tone,
  badgeVariant,
  badgeLabel,
  subtitle,
  deviceId,
  conflictMessage,
  ariaLabel,
}: ConflictHeaderProps) {
  const tc = toneClasses(tone);

  return (
    <div
      className={cn(
        GLASS,
        `p-5 bg-gradient-to-br ${accent.from} via-transparent ${accent.to} ring-1 ${accent.ring}`,
      )}
      role="region"
      aria-label={ariaLabel}
    >
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div>
          <div className={cn('flex items-center gap-2 text-[11px] uppercase tracking-wider', accent.text, accent.darkText)}>
            <span
              className={cn('inline-flex h-1.5 w-1.5 rounded-full', tc.icon, 'animate-pulse')}
              aria-hidden="true"
            />
            {entityLabel}
          </div>
          <h3 className="mt-1 text-xl font-bold text-neutral-900 dark:text-neutral-100">
            {identifier}
          </h3>
          {subtitle}
          {conflictMessage && (
            <p className="mt-1 text-xs text-neutral-600 dark:text-neutral-400 max-w-xl">
              {conflictMessage}
            </p>
          )}
        </div>
        <div className="flex flex-col items-end gap-2">
          {badgeLabel && (
            <Badge variant={badgeVariant} size="sm">
              {badgeLabel}
            </Badge>
          )}
          {deviceId && (
            <div className="text-[10px] uppercase tracking-wider text-neutral-500 dark:text-neutral-400">
              Device{' '}
              <span className="font-mono normal-case text-neutral-700 dark:text-neutral-300">
                {deviceId}
              </span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
